import type { UseAsyncSearchReturn } from "@circleco/compass/hooks/useAsyncSearch";

import { t } from "@/i18n-js/instance";

import type { CommunityMemberSelectVariantProps } from "./CommunityMemberSelectCompassComponent";
import { MultiMemberSelect } from "./MultiMemberSelect";
import { SingleMemberSelect } from "./SingleMemberSelect";
import type { CommunityMemberOption } from "./useCommunityMemberSelectOptions";

const I18N_ROOT = "community_member_select";

type CommunityMemberSelectViewProps = CommunityMemberSelectVariantProps & {
  isOpen: boolean;
  searchProps: UseAsyncSearchReturn<CommunityMemberOption>["searchProps"];
  setIsOpen: (next: boolean) => void;
};

// Rendering shared by every search variant. The variants differ only in which
// search hook they call, so they hand their hook result here.
export const CommunityMemberSelectView = ({
  addable = false,
  "data-testid": dataTestId = "community-member-selector",
  excludeAdmins = false,
  filters,
  getCommunityMemberText,
  includeInactive = false,
  isOpen,
  multiple = false,
  onChange,
  onSelectMember,
  searchProps,
  setIsOpen,
  value,
  valueField = "id",
  ...rest
}: CommunityMemberSelectViewProps) => {
  const hasSelection = Array.isArray(value) ? value.length > 0 : !!value;

  const sharedProps = {
    "data-testid": dataTestId,
    open: isOpen,
    onOpenChange: (next: boolean) => setIsOpen(next),
    creatable: addable,
    placeholder: t([I18N_ROOT, "placeholder"]),
    emptyMessage: t([I18N_ROOT, "empty_message"]),
    initialMessage: hasSelection ? "" : t([I18N_ROOT, "start_typing_a_name"]),
    searchPlaceholder: t([I18N_ROOT, "search_placeholder"]),
    creatableLabel: t([I18N_ROOT, "creatable_label"]),
    loadingMessage: t([I18N_ROOT, "loading_message"]),
    clearSelection: t([I18N_ROOT, "clean_selection"]),
  };

  if (multiple) {
    return (
      <MultiMemberSelect
        sharedProps={sharedProps}
        selectProps={rest}
        searchProps={searchProps}
        onChange={
          onChange as ((values: (number | string)[]) => void) | undefined
        }
        value={Array.isArray(value) ? value : []}
        excludeAdmins={excludeAdmins}
        filters={filters}
        getCommunityMemberText={getCommunityMemberText}
        includeInactive={includeInactive}
        onSelectMember={onSelectMember}
        valueField={valueField}
      />
    );
  }

  return (
    <SingleMemberSelect
      sharedProps={sharedProps}
      selectProps={rest}
      searchProps={searchProps}
      onChange={
        onChange as
          | ((value: number | string | null, displayText?: string) => void)
          | undefined
      }
      value={value as number | string | null | undefined}
      excludeAdmins={excludeAdmins}
      filters={filters}
      includeInactive={includeInactive}
      onSelectMember={onSelectMember}
      onSelectionComplete={() => setIsOpen(false)}
      valueField={valueField}
    />
  );
};
